/**
 * Steps Review Component
 * Shows generated steps and allows user to review/edit before code generation
 */

import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { apiClient } from '../services/api';
import { ChevronRight, ChevronDown } from 'lucide-react';

export function StepsReview() {
  const { state, dispatch } = useApp();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [showEditIcons, setShowEditIcons] = useState(false);

  const toggleStep = (stepIndex: number) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepIndex)) {
        newSet.delete(stepIndex);
      } else {
        newSet.add(stepIndex);
      }
      return newSet;
    });
  };

  const handleStepClick = (stepIndex: number) => {
    // Only allow clicking when code is ready
    if (state.status === 'code_ready' || state.status === 'executed' || state.status === 'executing') {
      dispatch({ type: 'SET_SELECTED_STEP_TAB', payload: stepIndex });
    }
  };

  const handleEditStep = (index: number) => {
    setEditingIndex(index);
    setEditDescription(state.steps[index].description);
    // Expand the step when editing
    if (!expandedSteps.has(state.steps[index].index)) {
      toggleStep(state.steps[index].index);
    }
  };

  const handleSaveEdit = (index: number) => {
    dispatch({
      type: 'UPDATE_STEP',
      payload: { index, step: { description: editDescription } },
    });
    setEditingIndex(null);
    setEditDescription('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditDescription('');
  };

  const handleConfirmAndGenerateCode = async () => {
    if (!state.sessionId) {
      dispatch({ type: 'SET_ERROR', payload: 'No session found' });
      return;
    }

    setLoading(true);
    dispatch({ type: 'SET_STATUS', payload: 'generating_code' });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      // Collect any step modifications
      const modifications = state.steps
        .map((step, index) => {
          // If step was edited, include it in modifications
          // For now, we'll send all steps (backend will handle updates)
          return null; // Simplified - can be enhanced
        })
        .filter(Boolean) as any[];

      const response = await apiClient.confirmSteps({
        session_id: state.sessionId,
        step_modifications: modifications.length > 0 ? modifications : undefined,
      });

      dispatch({ type: 'SET_FINAL_CODE', payload: response.final_code });
      
      // Fetch steps with code to get selenium_code for each step
      try {
        const stepsWithCode = await apiClient.getSteps(state.sessionId, true);
        dispatch({ type: 'SET_STEPS', payload: stepsWithCode });
      } catch (err) {
        console.warn('Failed to fetch step codes:', err);
      }
      
      dispatch({ type: 'SET_STATUS', payload: 'code_ready' });
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to generate code' });
      dispatch({ type: 'SET_STATUS', payload: 'steps_ready' });
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!state.sessionId) {
      dispatch({ type: 'SET_ERROR', payload: 'No session found' });
      return;
    }

    setExecuting(true);
    dispatch({ type: 'SET_STATUS', payload: 'executing' });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response = await apiClient.executeTest(state.sessionId);
      
      dispatch({ type: 'SET_EXECUTION_RESULT', payload: response });
      dispatch({ type: 'SET_STATUS', payload: 'executed' });

      if (!response.success) {
        dispatch({ type: 'SET_ERROR', payload: response.error || 'Test execution failed' });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to execute test' });
      dispatch({ type: 'SET_STATUS', payload: 'code_ready' });
    } finally {
      setExecuting(false);
    }
  };

  // Show steps when they're ready or after code is generated
  if (state.status !== 'steps_ready' && state.status !== 'code_ready' && state.status !== 'executed' && state.status !== 'executing') {
    return null;
  }

  return (
    <div className="steps-review p-4">
      {/* Test Plan Section */}
      <div className="stepsReviewHeader">
        <div className="flex items-center gap-2 ">
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2.55273 5.00191C2.5779 5.60642 2.81873 6.20345 3.28027 6.665C3.75478 7.13947 4.37236 7.38025 4.99414 7.39352C5.02279 8.06839 4.78285 8.75224 4.26758 9.26754C3.29125 10.2439 1.70875 10.2439 0.732422 9.26754C-0.243879 8.29121 -0.2439 6.7087 0.732422 5.73238C1.23401 5.23083 1.89546 4.98812 2.55273 5.00191ZM7.54102 5.00191C8.16706 5.01215 8.78987 5.25468 9.26758 5.73238C10.2439 6.70869 10.2438 8.2912 9.26758 9.26754C8.29125 10.2439 6.70875 10.2439 5.73242 9.26754C5.21738 8.75249 4.97652 8.06906 5.00488 7.39449C5.65892 7.40564 6.31636 7.16407 6.81543 6.665C7.27693 6.20348 7.51583 5.60637 7.54102 5.00191ZM5.00488 2.39938C4.97793 3.07232 5.21864 3.75376 5.73242 4.26754C6.23129 4.76638 6.88823 5.00798 7.54199 4.99703C7.54193 4.99867 7.54108 5.00027 7.54102 5.00191C6.8876 4.99124 6.23103 5.23381 5.73242 5.73238C5.27114 6.19367 5.03028 6.79034 5.00488 7.39449C5.00129 7.39443 4.99773 7.39359 4.99414 7.39352C4.96851 6.78971 4.72861 6.19341 4.26758 5.73238C3.79284 5.25766 3.17483 5.01497 2.55273 5.00191C2.55267 5.00029 2.5528 4.99866 2.55273 4.99703C3.17484 4.98397 3.79284 4.74227 4.26758 4.26754C4.78136 3.75375 5.02111 3.07234 4.99414 2.39938C4.99772 2.3993 5.0013 2.39944 5.00488 2.39938ZM0.732422 0.732384C1.70875 -0.243948 3.29125 -0.243948 4.26758 0.732384C4.73011 1.19493 4.96987 1.79354 4.99414 2.39938C4.37233 2.41263 3.7548 2.65535 3.28027 3.12984C2.76673 3.64339 2.52604 4.32438 2.55273 4.99703C1.89544 5.01083 1.23402 4.76912 0.732422 4.26754C-0.24391 3.29121 -0.243909 1.70872 0.732422 0.732384ZM5.73242 0.732384C6.70876 -0.243925 8.29125 -0.24394 9.26758 0.732384C10.2439 1.70871 10.2439 3.29122 9.26758 4.26754C8.79012 4.745 8.1677 4.98656 7.54199 4.99703C7.56869 4.32441 7.32893 3.64338 6.81543 3.12984C6.31636 2.63078 5.65891 2.38823 5.00488 2.39938C5.02915 1.79352 5.26987 1.19494 5.73242 0.732384Z" fill="url(#paint0_linear_15_1729)"/>
          <defs>
          <linearGradient id="paint0_linear_15_1729" x1="4.99981" y1="-3.8147e-05" x2="4.99981" y2="9.99962" gradientUnits="userSpaceOnUse">
          <stop stop-color="#0D12FF"/>
          <stop offset="1" stop-color="#080B99"/>
          </linearGradient>
          </defs>
        </svg>
          <h2 className="text-lg font-semibold text-gray-800">Test Plan</h2>
        </div>
        <div className="pl-4 ">
          <p className="text-gray-600 text-sm leading-relaxed stepsReviewTest">
            {state.testCase || 'No test case description provided'}
          </p>
        </div>
      </div>

      {/* Steps Section */}
      <div 
        className={`stepsViewWrap ${
          state.status === 'code_ready' || state.status === 'executed' || state.status === 'executing' 
            ? 'code-generated' 
            : ''
        }`}
        style={
          state.status === 'code_ready' || state.status === 'executed' || state.status === 'executing'
            ? { backgroundColor: '#f0f7f4' }
            : {}
        }
      >
      <div className="mb-4 ">
        <div className="flex items-center gap-2 ">
          <div className="">
          
          <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none">
            <circle cx="3" cy="3" r="3" fill="#080A99"/>
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800">Steps {state.steps.length}</h2>
        </div>
        
        {/* Instruction paragraph before steps */}
        {state.websiteUrl && (
          <div className="mb-4 flex">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4.07959 8.00166C4.11954 8.96925 4.50887 9.92498 5.24756 10.6638C6.00813 11.4243 6.99891 11.8128 7.99561 11.8327C8.04043 12.9112 7.65189 14.0043 6.82861 14.8278C5.26651 16.3899 2.73349 16.3899 1.17139 14.8278C-0.390524 13.2657 -0.390595 10.7336 1.17139 9.17159C1.97275 8.37022 3.02944 7.98082 4.07959 8.00166ZM12.0718 8.00069C13.0712 8.01862 14.065 8.40895 14.8276 9.17159C16.3897 10.7337 16.3897 13.2657 14.8276 14.8278C13.2655 16.3899 10.7335 16.3899 9.17139 14.8278C8.34812 14.0045 7.95887 12.912 8.00342 11.8337C9.05121 11.8526 10.1052 11.4633 10.9048 10.6638C11.6437 9.92475 12.032 8.96855 12.0718 8.00069ZM8.00342 3.83662C7.95971 4.91397 8.34886 6.00528 9.17139 6.82784C9.97081 7.62726 11.0241 8.01656 12.0718 7.99776V8.00069C11.0242 7.98189 9.9708 8.37218 9.17139 9.17159C8.4326 9.91038 8.04339 10.8661 8.00342 11.8337C8.00081 11.8336 7.99822 11.8328 7.99561 11.8327C7.95541 10.8656 7.56695 9.91008 6.82861 9.17159C6.06787 8.41084 5.07651 8.02145 4.07959 8.00166C4.07952 8.00004 4.07966 7.99841 4.07959 7.99678C5.0765 7.97699 6.06788 7.58857 6.82861 6.82784C7.65091 6.00532 8.03931 4.91383 7.99561 3.83662C7.99821 3.83657 8.00081 3.83667 8.00342 3.83662ZM1.17139 1.17159C2.73349 -0.390523 5.26651 -0.390523 6.82861 1.17159C7.56794 1.91108 7.95631 2.86813 7.99561 3.83662C6.9989 3.85656 6.00814 4.24694 5.24756 5.00752C4.42565 5.82955 4.03645 6.92015 4.07959 7.99678C3.02943 8.01762 1.97276 7.62921 1.17139 6.82784C-0.390543 5.26575 -0.390576 2.73365 1.17139 1.17159ZM9.17139 1.17159C10.7335 -0.390505 13.2655 -0.39047 14.8276 1.17159C16.3897 2.73369 16.3897 5.26573 14.8276 6.82784C14.065 7.59052 13.0713 7.97982 12.0718 7.99776C12.1152 6.92083 11.7269 5.82979 10.9048 5.00752C10.1052 4.20798 9.0512 3.8177 8.00342 3.83662C8.04272 2.86798 8.43184 1.91113 9.17139 1.17159Z" fill="url(#paint0_linear_15_1776)"/>
              <defs>
              <linearGradient id="paint0_linear_15_1776" x1="7.99966" y1="-0.000289917" x2="7.99966" y2="16.0001" gradientUnits="userSpaceOnUse">
              <stop stop-color="#0D12FF"/>
              <stop offset="1" stop-color="#080B99"/>
              </linearGradient>
              </defs>
              </svg>
            <p className="text-gray-700 text-sm leading-relaxed">
              Open <span className="font-semibold">{state.websiteUrl}</span> and make the following steps
            </p>
          </div>
        )}
      </div>

      <div className="steps-list space-y-2">
        {state.steps.map((step, index) => {
          const isExpanded = expandedSteps.has(step.index);
          const isEditing = editingIndex === index;
          
          return (
            <div 
              key={step.index} 
              className="bg-gray-100 border border-gray-200 rounded-lg overflow-hidden singleStepWrapper"
            >
              <button
                onClick={() => toggleStep(step.index)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-200 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-600 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
                )}
                <span className="text-gray-800 flex-1">{step.description}</span>
                {!isEditing && state.status === 'steps_ready' && showEditIcons && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditStep(index);
                    }}
                    className="p-1 hover:bg-gray-300 rounded transition-colors"
                    title="Edit step"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-pencil">
                      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"></path>
                      <path d="m15 5 4 4"></path>
                    </svg>
                  </button>
                )}
              </button>
              
              {isExpanded && (
                <div className="border-t border-gray-200 p-4">
                  {isEditing ? (
                    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(index)}
                          className="saveBtn px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="cancelBtn px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-600 text-sm">
                      {step.description}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {state.status === 'steps_ready' && (
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => setShowEditIcons(!showEditIcons)}
            className="editStepsButton px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2 font-medium"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"></path>
              <path d="m15 5 4 4"></path>
            </svg>
            Edit
          </button>
          <button
            type="button"
            onClick={handleConfirmAndGenerateCode}
            disabled={loading}
            className=" ApproveButton px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center whitespace-nowrap text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? '🔄 Generating Code...' : 'Approve'}
          </button>
        </div>
      )}

      {(state.status === 'code_ready' || state.status === 'executed' || state.status === 'executing') && (
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleExecute}
            disabled={executing || state.status === 'executing'}
            className="executeTestButton px-4 py-2 bg-[#e76e50] text-white rounded-lg hover:bg-[#d65a40] transition-colors flex items-center justify-center whitespace-nowrap text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {executing || state.status === 'executing' ? '🔄 Executing...' : 'Execute Test'}
          </button>
        </div>
      )}

      {state.error && (
        <div className="error-message mt-2">
          ❌ {state.error}
        </div>
      )}
      </div>
    </div>
  );
}
